/**
 * RideRouter — Deterministic traffic split for ride booking.
 * Routes users to /ride/book (map-first) or /ride/standard based on rollout config.
 * Same user always sees same flow (hash-based bucketing).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function hashBucket(userId) {
  // Simple deterministic hash (matches backend md5 bucketing)
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    const char = userId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash) % 100;
}

export default function RideRouter() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const route = async () => {
      try {
        // Check rollout config
        const res = await fetch(`${API_URL}/api/rollout/ride-booking`);
        const data = await res.json();
        const percentage = data?.config?.percentage || 0;

        if (percentage <= 0) {
          navigate('/ride/standard', { replace: true });
          return;
        }
        if (percentage >= 100) {
          navigate('/ride/book', { replace: true });
          return;
        }

        // Deterministic bucketing — check if user is logged in
        const token = localStorage.getItem('token');
        if (token) {
          try {
            const checkRes = await fetch(`${API_URL}/api/rollout/ride-booking/check`, {
              headers: { 'Authorization': `Bearer ${token}` },
            });
            const checkData = await checkRes.json();
            if (checkData.success) {
              navigate(checkData.flow === 'map_first' ? '/ride/book' : '/ride/standard', { replace: true });
              return;
            }
          } catch (e) { /* fall through to client-side bucketing */ }
        }

        // Client-side bucketing for non-authenticated users
        const visitorId = localStorage.getItem('visitor_id') || (Math.random().toString(36).slice(2) + Date.now().toString(36));
        localStorage.setItem('visitor_id', visitorId);
        const bucket = hashBucket(visitorId);
        navigate(bucket < percentage ? '/ride/book' : '/ride/standard', { replace: true });
      } catch (e) {
        // Fallback to standard on any error
        navigate('/ride/standard', { replace: true });
      }
      setChecking(false);
    };
    route();
  }, [navigate]);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }
  return null;
}
